import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import Select from "react-select";
import { Offcanvas } from "bootstrap";
import { hasEditAccess } from "./CheckAccess";

const SidebarForm = forwardRef(
  (
    { id, title, initialValues, fields, onSubmit, onClear, selectedItem,permission="restaurant" },
    ref
  ) => {
    const [formData, setFormData] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [isEditable, setIsEditable] = useState(true);
    const [resetPrev,setResetPrev]= useState(false)

     const [permissions, setPermissions] = useState([]);
        
          const existingPermissions = localStorage.getItem("checkPermission");
        
          useEffect(() => {
            if (existingPermissions != undefined) {
              const parsedData = JSON.parse(existingPermissions);
              setPermissions(parsedData);
            }
          }, [existingPermissions]);
          
    useEffect(() => {
      if (selectedItem) {
        setFormData(selectedItem);
        setIsEditable(false);
      } else {
        setFormData(initialValues);
        setIsEditable(true);
      }
    }, [selectedItem, resetPrev]);

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSelectChange = (name, selectedOption) => {
      const value = selectedOption ? selectedOption.value : "";
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleMultiSelectChange = (name, selectedOptions) => {
      const value = selectedOptions
        ? selectedOptions.map((opt) => opt.value)
        : [];
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validate = () => {
      const newErrors = {};
      fields.forEach(({ name, label, required }) => {
        if (required) {
          const value = formData[name];
          // Check for empty values including empty arrays for multiselect
          if (
            !value ||
            (Array.isArray(value) && value.length === 0) ||
            value === ""
          ) {
            newErrors[name] = `${label} is required.`;
          }
        }
      });
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
      if (validate()) {
        const isEdit = !!selectedItem?.id;
        onSubmit(formData, isEdit);
        handleClear();

        // Close the sidebar after submit
        if (ref?.current?.closeSidebar) {
          ref.current.closeSidebar();
        }
      }
    };


    const handleClear = () => {
      selectedItem ? setResetPrev((prev)=>!prev): setFormData(initialValues);
      setErrors({});
      setIsEditable(true);
      onClear?.();
    };

    useImperativeHandle(ref, () => ({
      closeSidebar: () => {
        const sidebarElement = document.getElementById(id);
        if (sidebarElement) {
          const bsOffcanvas =
            Offcanvas.getInstance(sidebarElement) ||
            new Offcanvas(sidebarElement);

          bsOffcanvas.hide();

          // Cleanup backdrop immediately
          const backdrop = document.querySelector(".offcanvas-backdrop");
          if (backdrop) backdrop.remove();

          document.body.classList.remove(
            "offcanvas-backdrop",
            "show",
            "modal-open"
          );
        }
      },

      openSidebar: () => {
        const sidebarElement = document.getElementById(id);
        if (sidebarElement) {
          const bsOffcanvas =
            Offcanvas.getInstance(sidebarElement) ||
            new Offcanvas(sidebarElement);
          bsOffcanvas.show();

          sidebarElement.addEventListener(
            "hidden.bs.offcanvas",
            () => {
              const backdrop = document.querySelector(".offcanvas-backdrop");
              if (backdrop) backdrop.remove();
              document.body.classList.remove(
                "offcanvas-backdrop",
                "show",
                "modal-open"
              );
            },
            { once: true }
          );
        }
      },
    }));

    // Custom styles for react-select error state
    const getSelectStyles = (hasError) => ({
      control: (provided, state) => ({
        ...provided,
        borderColor: hasError ? "#dc3545" : provided.borderColor,
        boxShadow: hasError
          ? "0 0 0 0.2rem rgba(220, 53, 69, 0.25)"
          : provided.boxShadow,
        "&:hover": {
          borderColor: hasError ? "#dc3545" : provided["&:hover"].borderColor,
        },
      }),
    });

    return (
      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id={id}
        aria-labelledby={id}
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">{title}</h5>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={()=>{handleClear()}}
          />
        </div>
        <div className="offcanvas-body">
          {fields.map((field, i) => {
            const { name, label, type, options, required } = field;
            const hasError = !!errors[name];

            return (
              <div className="form-group mb-3" key={i}>
                <label className="form-label text-muted">
                  {label}
                  {required && <span className="text-danger">*</span>}
                </label>

                {type === "select" ? (
                  <>
                    <Select
                      name={name}
                      isDisabled={!isEditable}
                      options={options || []}
                      classNamePrefix="react-select"
                      styles={getSelectStyles(hasError)}
                      value={
                        options?.find((opt) => opt.value === formData[name]) ||
                        null
                      }
                      onChange={(selectedOption) =>
                        handleSelectChange(name, selectedOption)
                      }
                      placeholder={`Select ${label}`}
                    />
                    {hasError && (
                      <div className="text-danger small mt-1">
                        {errors[name]}
                      </div>
                    )}
                  </>
                ) : type === "multiselect" ? (
                  <>
                    {options?.length > 0 ? (
                      <Select
                        isMulti
                        name={name}
                        isDisabled={!isEditable}
                        options={options}
                        classNamePrefix="react-select"
                        styles={getSelectStyles(hasError)}
                        value={(formData[name] || [])
                          .map((val) => {
                            const option = options.find(
                              (opt) => opt.value === val
                            );
                            return option
                              ? { label: option.label, value: option.value }
                              : null;
                          })
                          .filter(Boolean)}
                        onChange={(selectedOptions) =>
                          handleMultiSelectChange(name, selectedOptions)
                        }
                        placeholder={`Select ${label}`}
                      />
                    ) : (
                      <div className="text-muted">Loading options...</div>
                    )}
                    {hasError && (
                      <div className="text-danger small mt-1">
                        {errors[name]}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      type={type}
                      name={name}
                      readOnly={!!field.readOnly}
                      disabled={!isEditable || !!field.readOnly}
                      className={`form-control ${hasError ? "is-invalid" : ""}`}
                      placeholder={`Enter ${label}`}
                      value={field.readOnly ? (field.value ?? formData[name] ?? "") : (formData[name] ?? "")}
                      onChange={handleChange}
                    />
                    {hasError && (
                      <div className="invalid-feedback d-block">
                        {errors[name]}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/* Conditional Buttons */}
        {hasEditAccess(permissions, permission) &&
         ( <div className="d-flex">
            {isEditable ? (
              <>
                <button
                  type="button"
                  className="btn w-100 me-1 py-2 btn-primary"
                  onClick={handleClear}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn w-100 ms-1 py-2 btn-dark"
                  onClick={handleSubmit}
                >
                  {selectedItem ? "Update" : "Save"}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn w-100 btn-outline-dark"
                onClick={() => setIsEditable(true)}
              >
                Edit
              </button>
            )}
          </div>)}
        </div>
      </div>
    );
  }
);

export default SidebarForm;
